/**
 * The conversations about this document, beside it.
 *
 * BESIDE THE TEXT, NOT OVER IT. A comment is read against the sentence it is about, so the panel
 * sits in the same place the version history does and the document stays visible. Clicking a
 * thread selects its anchor in the page; clicking anchored text opens its thread.
 *
 * RESOLVED THREADS ARE HIDDEN, NOT DELETED, and the toggle says how many. An inspection asks what
 * was raised and how it was answered -- a panel that dropped the answered ones would make a
 * reviewed document look unreviewed.
 *
 * ANYBODY WHO CAN SEE THE DOCUMENT CAN COMMENT, including on a revision that is out for approval
 * and cannot be edited. The server agrees: a comment's anchor is stripped before the content digest
 * is taken, so asking a question changes nothing about what an approval covers.
 */
import React from "react";
import {
    Alert, Box, Button, CircularProgress, Divider, IconButton, TextField, Tooltip, Typography,
} from "@mui/material";

import MentionField from "./MentionField";
import { mentionRanges, stillNamed } from "./mentions";
import type { Person } from "./mentions";
import Symbol from "./Symbol";
import { CHROME_FONT } from "./EditorToolbar";
import { colourFor } from "./suggestions";
import { Grey3, Grey5, Grey17, Blue3 } from "../../theme/styles";

export type CommentMention = { AccountUserUSID: string; Name: string | null };

export type CommentMessage = {
    USID: string;
    Body: string;
    /** Who this message names. Stored beside the text, never parsed out of it. */
    Mentions?: CommentMention[];
    ByUSID: string | null;
    ByName: string | null;
    At: string;
};

export type CommentThread = {
    USID: string;
    AnchorText: string | null;
    Resolved: boolean;
    ResolvedBy: string | null;
    ByUSID: string | null;
    ByName: string | null;
    At: string;
    Messages: CommentMessage[];
};

/**
 * A message with the people it names picked out.
 *
 * DRAWN FROM THE STORED MENTIONS, not by looking for "@" in the text. The server says who was
 * named; the text merely happens to contain their name, and an "@" in a comment on a procedure is
 * usually something else entirely -- @121°C, an address, a part number. Highlighting those would
 * invent mentions that were never made.
 */
function withNames(message: CommentMessage): React.ReactNode {
    const named = message.Mentions ?? [];
    if (!named.length) return message.Body;

    const ranges = mentionRanges(message.Body, named);
    if (!ranges.length) return message.Body;

    const out: React.ReactNode[] = [];
    let at = 0;
    ranges.forEach((range, index) => {
        if (range.start > at) out.push(message.Body.slice(at, range.start));
        out.push(
            <Box key={index} component="span"
                 sx={{ color: Blue3, fontWeight: 500 }}>
                {message.Body.slice(range.start, range.end)}
            </Box>);
        at = range.end;
    });
    if (at < message.Body.length) out.push(message.Body.slice(at));
    return out;
}

function when(at: string): string {
    const date = new Date(at);
    const sameDay = date.toDateString() === new Date().toDateString();
    return sameDay
        ? date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
        : date.toLocaleString([], { month: "short", day: "numeric",
                                    hour: "numeric", minute: "2-digit" });
}

export default function CommentPanel({ threads, loading, problem, active, onSelect, onReply,
                                       onResolve, onDismissProblem, onClose, people }: {
    threads: CommentThread[] | null;
    loading: boolean;
    problem: string | null;
    /** The thread the caret is in, or the one just opened. */
    active: string | null;
    onSelect: (thread: CommentThread) => void;
    onReply: (thread: CommentThread, body: string, mentions: string[]) => Promise<void>;
    /** Who the "@" picker may offer, for the reply box. */
    people: (search: string) => Promise<Person[]>;
    onResolve: (thread: CommentThread, resolved: boolean) => Promise<void>;
    onDismissProblem: () => void;
    onClose: () => void;
}) {
    const [showResolved, setShowResolved] = React.useState(false);
    const [replying, setReplying] = React.useState<
        { usid: string; text: string; chosen: Person[] } | null>(null);
    const [busy, setBusy] = React.useState<string | null>(null);

    const all = threads ?? [];
    const open = all.filter(one => !one.Resolved);
    const resolved = all.filter(one => one.Resolved);
    const shown = showResolved ? all : open;

    const send = async (thread: CommentThread, body: string, chosen: Person[]) => {
        setBusy(thread.USID);
        try {
            // CHECKED AGAINST THE TEXT BEING SENT. Somebody can pick a name and then delete it
            // again, and a mention of a person the sentence does not name is how somebody gets
            // told about a document for no reason they can see.
            await onReply(thread, body, stillNamed(body, chosen).map(p => p.AccountUserUSID));
            setReplying(null);
        } finally {
            setBusy(null);
        }
    };

    return (
        <Box sx={{ width: 320, flexShrink: 0, borderLeft: `1px solid ${Grey3}`,
                   display: "flex", flexDirection: "column", fontFamily: CHROME_FONT,
                   maxHeight: "calc(100vh - 160px)", position: "sticky", top: 8 }}>

            <Box sx={{ display: "flex", alignItems: "center", px: 1.5, py: 1 }}>
                <Typography sx={{ fontSize: 15, color: Grey17, fontFamily: CHROME_FONT, flexGrow: 1 }}>
                    Comments{open.length ? ` (${open.length})` : ""}
                </Typography>
                {resolved.length > 0 && (
                    <Tooltip title={showResolved
                        ? "Hide the resolved ones"
                        : `Show ${resolved.length} resolved`}>
                        <IconButton size="small" onClick={() => setShowResolved(v => !v)}
                                    aria-label="Show resolved comments" aria-pressed={showResolved}>
                            <Symbol name={showResolved ? "check_circle" : "check_circle_outline"}
                                    size={18} colour={showResolved ? Blue3 : Grey5}/>
                        </IconButton>
                    </Tooltip>
                )}
                <IconButton size="small" onClick={onClose} aria-label="Close comments">
                    <Symbol name="close" size={18}/>
                </IconButton>
            </Box>
            <Divider/>

            {problem && (
                <Alert severity="info" sx={{ m: 1 }} onClose={onDismissProblem}>{problem}</Alert>
            )}

            <Box sx={{ overflowY: "auto", flexGrow: 1 }}>
                {loading && threads === null ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                        <CircularProgress size={20}/>
                    </Box>
                ) : shown.length === 0 ? (
                    <Typography sx={{ fontSize: 12.5, color: Grey5, p: 2, fontFamily: CHROME_FONT }}>
                        {all.length === 0
                            ? "No comments yet. Select some text and choose Insert ▸ Comment to ask about it."
                            : "Nothing outstanding. Everything raised has been answered."}
                    </Typography>
                ) : shown.map(thread => {
                    const here = active === thread.USID;
                    return (
                        <Box key={thread.USID}
                             role="button" tabIndex={0}
                             aria-label={`Comment from ${thread.ByName ?? "someone"}`}
                             onClick={() => onSelect(thread)}
                             sx={{ px: 1.5, py: 1.25, borderBottom: "1px solid #F1F3F4",
                                   cursor: "pointer",
                                   backgroundColor: here ? "#E8F0FE" : "transparent",
                                   opacity: thread.Resolved ? 0.65 : 1,
                                   "&:hover": { backgroundColor: here ? "#E8F0FE" : "#F8F9FA" } }}>

                            {/* WHAT IT WAS ABOUT, kept with the thread rather than read from the
                                document -- so a thread whose sentence has since been deleted can
                                still say what it was asking about. */}
                            {thread.AnchorText && (
                                <Typography sx={{ fontSize: 11.5, color: Grey5, mb: 0.75,
                                                  fontFamily: CHROME_FONT, borderLeft: `2px solid ${Grey3}`,
                                                  pl: 1, fontStyle: "italic",
                                                  overflow: "hidden", textOverflow: "ellipsis",
                                                  whiteSpace: "nowrap" }}>
                                    {thread.AnchorText}
                                </Typography>
                            )}

                            {thread.Messages.map(message => (
                                <Box key={message.USID} sx={{ mb: 1 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                                                   backgroundColor: colourFor(message.ByUSID || "") }}/>
                                        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: Grey17,
                                                          fontFamily: CHROME_FONT }}>
                                            {message.ByName ?? "Someone"}
                                        </Typography>
                                        <Typography sx={{ fontSize: 11, color: Grey5, fontFamily: CHROME_FONT }}>
                                            {when(message.At)}
                                        </Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: 13, color: Grey17, ml: 2, mt: 0.25,
                                                      fontFamily: CHROME_FONT, whiteSpace: "pre-wrap" }}>
                                        {withNames(message)}
                                    </Typography>
                                </Box>
                            ))}

                            {thread.Resolved && (
                                <Typography sx={{ fontSize: 11.5, color: Grey5, ml: 2,
                                                  fontFamily: CHROME_FONT }}>
                                    Resolved{thread.ResolvedBy ? ` by ${thread.ResolvedBy}` : ""}
                                </Typography>
                            )}

                            {replying?.usid === thread.USID ? (
                                <Box sx={{ ml: 2, mt: 0.5 }} onClick={e => e.stopPropagation()}>
                                    <MentionField
                                        autoFocus minRows={1} placeholder="Reply — type @ to name somebody"
                                        value={replying.text}
                                        chosen={replying.chosen}
                                        people={people}
                                        onChange={(text) => setReplying({ usid: thread.USID, text,
                                                                          chosen: replying.chosen })}
                                        onChosen={(chosen) => setReplying({ usid: thread.USID,
                                                                            text: replying.text, chosen })}
                                        onSubmit={() => {
                                            if (replying.text.trim()) {
                                                void send(thread, replying.text.trim(), replying.chosen);
                                            }
                                        }}/>
                                    <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
                                        <Button size="small" disabled={!replying.text.trim() || !!busy}
                                                onClick={() => send(thread, replying.text.trim(),
                                                                    replying.chosen)}
                                                sx={{ fontSize: 12, fontFamily: CHROME_FONT }}>Reply</Button>
                                        <Button size="small" onClick={() => setReplying(null)}
                                                sx={{ fontSize: 12, color: Grey5, fontFamily: CHROME_FONT }}>
                                            Cancel
                                        </Button>
                                    </Box>
                                </Box>
                            ) : (
                                <Box sx={{ display: "flex", gap: 0.5, ml: 1.5 }}
                                     onClick={e => e.stopPropagation()}>
                                    <Button size="small"
                                            onClick={() => setReplying({ usid: thread.USID, text: "", chosen: [] })}
                                            sx={{ fontSize: 12, color: Grey17, fontFamily: CHROME_FONT }}>
                                        Reply
                                    </Button>
                                    {/* RESOLVING HIDES NOTHING. The thread keeps its messages and
                                        its authors; it stops being outstanding. */}
                                    <Button size="small" disabled={busy === thread.USID}
                                            onClick={() => onResolve(thread, !thread.Resolved)}
                                            sx={{ fontSize: 12, color: Grey5, fontFamily: CHROME_FONT }}>
                                        {thread.Resolved ? "Reopen" : "Resolve"}
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}
